/**
 * Reading one dictionary in one language, with total fallback to English.
 *
 * ONE RULE, and it is the same rule modules/forms/i18n.js was built on: **a missing translation shows
 * the base language.** Never a blank, never a key. A tenant will always translate most of a thing and
 * leave the rest, and the rest is never the part you would have chosen — so every lookup uses `||`
 * and treats PRESENT-BUT-EMPTY exactly like absent.
 *
 * That rule is what makes partial coverage safe, and therefore what makes app-wide translation
 * shippable a screen at a time instead of as one 4,700-phrase big bang. There are ~690 visible text
 * nodes in this front end; if an uncovered one rendered `portal.timekeeping` or an empty box, nobody
 * could ever ship the first screen.
 *
 * This is `chrome(lang)` from modules/forms/i18n.js, generalized on exactly one axis: the dictionary
 * is an argument instead of a module constant, so every namespace can have one.
 */
import { BASE_LANGUAGE, primary } from './languages'

/**
 * A getter for `dict` in `lang`, falling back string-by-string to the base language.
 *
 * @param {object} dict  {lang: {id: string | (…args) => string}}
 * @param {string} lang  the language to read in; a regional tag resolves to its language
 * @returns {(id: string, ...args: any[]) => string}
 *
 * A VALUE MAY BE A FUNCTION, and for anything with a number in it, it must be. "6 of 8 answered" is a
 * count, not a sentence, and the number does not have to land in the same place in another language;
 * a concatenation hardcodes English word order into every translation that follows. Same pattern as
 * `answeredCount` on the QR page.
 */
export function makeT(dict, lang) {
  const base = (dict && dict[BASE_LANGUAGE]) || {}
  const chosen = (dict && (dict[lang] || dict[primary(lang)])) || {}
  return (id, ...args) => {
    // NO ID AT ALL IS THE DOCUMENTED HOUSE PATTERN, not a mistake: `t(REGISTRY[x]) || fallback`
    // resolves a label for a value the front end may never have heard of (shared/constants/modules.js,
    // AppHeader.jsx, the attendance and absence-reason tables in the timekeeping grid). A registry
    // miss hands this function `undefined` on purpose and takes the fallback — so shouting about it
    // would train people to ignore the one message below that means something.
    if (typeof id !== 'string' || !id) return ''
    const own = chosen[id]
    const v = own !== undefined && own !== '' ? own : base[id]
    if (v === undefined) {
      // Absent from the BASE dictionary too, so there is nothing to fall back TO. That is a coding
      // mistake rather than a missing translation — an untranslated string is normal and safe, a
      // string with no English original is neither. Loud in dev, silent (and blank, never a key) in
      // production: shouting an id at somebody standing in a yard helps nobody.
      if (import.meta.env?.MODE !== 'production') {
        console.error(`[i18n] '${id}' has no English original — a string with no base is a bug, not a missing translation.`)
      }
      return ''
    }
    return typeof v === 'function' ? v(...args) : v
  }
}
